import { Link } from 'react-router-dom';
import koraIcon from '@/assets/imgs/kora-icon.svg';

const ULTIMA_ATUALIZACAO = '11 de setembro de 2026';
const RESPONSAVEL = 'RMBS Logistica';
const EMAIL_CONTATO = 'marcio@daxossistemas.com.br';

export default function PoliticaPrivacidade() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-5">
          <Link to="/" className="flex items-center gap-2">
            <img src={koraIcon} alt="Kora" className="h-8 w-8" />
            <span className="text-lg font-semibold">Kora</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="text-3xl font-bold tracking-tight">Política de Privacidade</h1>
        <p className="mt-2 text-sm text-muted-foreground">Última atualização: {ULTIMA_ATUALIZACAO}</p>

        <div className="prose prose-neutral mt-8 max-w-none dark:prose-invert prose-headings:font-semibold prose-h2:mt-8 prose-h2:text-xl prose-p:leading-relaxed prose-li:leading-relaxed">
          <p>
            Esta Política de Privacidade descreve como o <strong>{RESPONSAVEL}</strong> ("nós"), responsável pela
            plataforma Kora, coleta, usa, armazena e protege os dados de quem utiliza o sistema.
          </p>

          <h2>1. Quem é o responsável</h2>
          <p>
            {RESPONSAVEL} é o responsável pelo tratamento dos dados descritos nesta política. Dúvidas, solicitações
            ou pedidos relacionados a dados pessoais podem ser enviados para{' '}
            <a href={`mailto:${EMAIL_CONTATO}`}>{EMAIL_CONTATO}</a>.
          </p>

          <h2>2. Quais dados coletamos</h2>
          <p>Para operar a plataforma, coletamos e armazenamos:</p>
          <ul>
            <li>Dados de conta: nome, e-mail e empresa vinculada.</li>
            <li>
              Dados de uso: informações inseridas nos módulos do sistema (OKRs, cronogramas, CRM, formulários,
              organograma e calendário), incluindo eventos, tarefas, anexos e comentários criados pelo usuário.
            </li>
            <li>Registros técnicos: logs de acesso e auditoria, necessários para segurança e suporte.</li>
          </ul>

          <h2>3. Integração com o Google Calendar</h2>
          <p>
            O módulo de Calendário oferece, de forma opcional e mediante autorização explícita do usuário, a
            sincronização com o Google Calendar. Ao conectar sua conta Google, o Kora solicita acesso aos seguintes
            escopos:
          </p>
          <ul>
            <li><code>https://www.googleapis.com/auth/calendar</code> — ler e escrever eventos em um calendário dedicado, criado na própria conta Google do usuário.</li>
            <li><code>openid</code> e <code>email</code> — apenas para identificar o e-mail da conta conectada.</li>
          </ul>
          <p>Com essa autorização, o Kora:</p>
          <ul>
            <li>
              Cria um calendário dedicado (separado dos demais calendários pessoais do usuário) na conta Google
              autorizada, usado exclusivamente para espelhar os eventos do Kora.
            </li>
            <li>
              Sincroniza, nos dois sentidos, apenas os eventos dos calendários do Kora que o próprio usuário optou
              por sincronizar — título, descrição, local, data/horário e participantes do evento.
            </li>
            <li>
              Armazena o token de acesso do Google de forma cifrada (AES-256) em nosso banco de dados, usado
              somente para realizar a sincronização em nome do próprio usuário.
            </li>
          </ul>
          <p>
            O uso e a transferência, pelo Kora, de informações recebidas das APIs do Google seguem a{' '}
            <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">
              Política de Dados do Usuário dos Serviços de API do Google
            </a>
            , incluindo os requisitos de Uso Limitado ("Limited Use"). Os dados obtidos via Google Calendar API não
            são usados para fins de publicidade, não são vendidos, e não são compartilhados com terceiros, exceto
            quando estritamente necessário para prestar o próprio serviço de sincronização ao usuário.
          </p>
          <p>
            A qualquer momento, o usuário pode desconectar a integração na tela de Calendário do Kora ("Desconectar
            Google"). Ao desconectar, o token de acesso é revogado e removido do banco de dados; o calendário
            dedicado criado na conta Google do usuário permanece sob controle dele, que pode excluí-lo diretamente
            pelo Google Calendar.
          </p>

          <h2>4. Como usamos os dados</h2>
          <ul>
            <li>Para operar as funcionalidades da plataforma contratada pela empresa do usuário.</li>
            <li>Para enviar notificações e lembretes relacionados a prazos, tarefas e eventos.</li>
            <li>Para dar suporte técnico quando solicitado.</li>
            <li>Para fins de segurança, auditoria e prevenção de fraude.</li>
          </ul>

          <h2>5. Compartilhamento de dados</h2>
          <p>
            Não vendemos nem compartilhamos dados pessoais com terceiros para fins de marketing. Dados podem ser
            processados por fornecedores de infraestrutura estritamente necessários à operação do sistema (ex.:
            hospedagem, armazenamento de arquivos e envio de e-mails transacionais), sempre limitados ao necessário
            para prestar o serviço.
          </p>

          <h2>6. Armazenamento e segurança</h2>
          <p>
            Os dados são armazenados em banco de dados protegido por controle de acesso. Tokens de integração
            externa (como o do Google Calendar) são cifrados em repouso. Adotamos medidas técnicas razoáveis para
            proteger os dados contra acesso não autorizado, perda ou alteração indevida.
          </p>

          <h2>7. Seus direitos</h2>
          <p>
            Nos termos da Lei Geral de Proteção de Dados (LGPD), o usuário pode solicitar, a qualquer momento,
            confirmação de tratamento, acesso, correção, anonimização ou exclusão de seus dados pessoais, entrando
            em contato pelo e-mail <a href={`mailto:${EMAIL_CONTATO}`}>{EMAIL_CONTATO}</a>.
          </p>

          <h2>8. Alterações nesta política</h2>
          <p>
            Esta política pode ser atualizada periodicamente. A data da última atualização é sempre indicada no
            topo desta página.
          </p>

          <h2>9. Contato</h2>
          <p>
            Dúvidas sobre esta política ou sobre o tratamento de dados podem ser enviadas para{' '}
            <a href={`mailto:${EMAIL_CONTATO}`}>{EMAIL_CONTATO}</a>.
          </p>
        </div>
      </main>
    </div>
  );
}
